import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import ConfirmationDialog from '../../../core-components/theme/components/DialogBox/ConfirmationDialog';
import type { RowModalProps } from '../../../components/TableActions';
import { LOGBOOK_DELETED } from '../../../modules/analytics/events';
import { trackEvent } from '../../../modules/analytics/trackingMiddleware';
import { useDeleteLogbookTemplate } from '../hooks/useDeleteLogbook';
import { LOGBOOK_TEMPLATES_KEY } from '../hooks/useLogbookTemplateTable';
import type { LogbookTemplate, LogbookTemplatesPage } from '../types/loogBookTypes';

type TemplatePages = InfiniteData<LogbookTemplatesPage>;

const withoutTemplate = (pages: TemplatePages | undefined, templateId: string): TemplatePages | undefined =>
  pages && {
    ...pages,
    pages: pages.pages.map((page, index) => ({
      ...page,
      logbookTemplates: page.logbookTemplates.filter((template) => template.logbookTemplateId !== templateId),
      totalCount: index === 0 ? page.totalCount - 1 : page.totalCount,
    })),
  };

export default function DeleteLogbookModal({ handleClose, data }: RowModalProps<LogbookTemplate>) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteLogbookTemplate(
    {
      onMutate: async (templateId: string) => {
        await queryClient.cancelQueries({ queryKey: LOGBOOK_TEMPLATES_KEY });
        const previous = queryClient.getQueriesData<TemplatePages>({ queryKey: LOGBOOK_TEMPLATES_KEY });
        queryClient.setQueriesData<TemplatePages>({ queryKey: LOGBOOK_TEMPLATES_KEY }, (pages) => withoutTemplate(pages, templateId));
        return { previous };
      },
      onError: (err: Error, _templateId: string, context: any) => {
        context?.previous?.forEach(([queryKey, pages]: [readonly unknown[], TemplatePages | undefined]) =>
          queryClient.setQueryData<TemplatePages>(queryKey, pages),
        );
        toast.error(err?.message);
      },
    },
    LOGBOOK_TEMPLATES_KEY,
  );

  const deleteTemplate = () => {
    deleteMutation.mutate(data.logbookTemplateId);
    trackEvent(LOGBOOK_DELETED, { logbookTemplateId: data.logbookTemplateId, logbookName: data.name || '' });
  };

  const actions = [
    { text: t('common.cancel', 'Cancel'), handler: handleClose, color: 'Neutrals.100', textColor: 'Neutrals.900' },
    {
      text: t('common.delete', 'Delete'),
      handler: deleteTemplate,
      color: 'SemanticError.500',
      textColor: 'common.white',
      disabled: deleteMutation.isPending,
    },
  ];

  return (
    <ConfirmationDialog
      open={true}
      actions={actions}
      handleClose={handleClose}
      title={t('logbook.deleteLogbookTitle', 'Delete Logbook')}
      description={t('logbook.deleteLogbookDescription', 'It will be deleted from everywhere it is used, are you sure you want to delete it?')}
    />
  );
}
